import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Text,
    Button,
    Flex,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
    VStack,
    useMultiStyleConfig,
    useClipboard,
} from '@chakra-ui/react'
import {PromoIcon, PromoCopyIcon, GalleryChevronRightIcon, CheckIcon} from '../icons'

const getPromoVariantStyle = (bannerData, styles) => {
    const backgroundColor = bannerData?.bannerBackgroundColor?.toLowerCase()

    if (backgroundColor === 'red') {
        return styles.promoCardRed
    }

    if (backgroundColor === 'grey' || backgroundColor === 'gray') {
        return styles.promoCardGrey
    }

    if (bannerData?.isRed) {
        return styles.promoCardRed
    }

    if (bannerData?.isGrey) {
        return styles.promoCardGrey
    }

    return styles.promoCardDefault
}

const isRedBanner = (bannerData) => {
    const backgroundColor = bannerData?.bannerBackgroundColor?.toLowerCase()

    return backgroundColor === 'red' || bannerData?.isRed
}

const isGreyBanner = (bannerData) => {
    const backgroundColor = bannerData?.bannerBackgroundColor?.toLowerCase()

    return backgroundColor === 'grey' || backgroundColor === 'gray' || bannerData?.isGrey
}

const getPromoSummaryBackgroundStyle = (bannerData, showImage, hasPromoImage) => {
    if (showImage && hasPromoImage && isGreyBanner(bannerData)) {
        return {
            bg: 'background.base.under',
        }
    }

    if (isRedBanner(bannerData)) {
        return {
            bg: 'background.negative.muted',
        }
    }

    if (isGreyBanner(bannerData)) {
        return {
            bg: 'background.base.under',
        }
    }

    return {}
}

const PromoSummaryContent = ({
    styles,
    bannerData,
    multiPromoCount,
    headingText,
    bannerDescription,
    showChevron,
    showCode,
    bannerCode,
    showImage,
    onFocusLinkClick,
}) => {
    const {onCopy, hasCopied} = useClipboard(bannerCode || '')
    // Only apply background style for drawer items (showImage=true) and single promos (showChevron=false)
    // For multi-promo button trigger (showChevron=true), use theme slots only
    const shouldApplyBackgroundStyle = !showChevron
    const isGreyPromo = isGreyBanner(bannerData)
    const isRedPromo = isRedBanner(bannerData)
    const bannerTag = bannerData?.bannerTag
    const hasPrimaryBannerImage = Boolean(
        bannerData?.bannerImageDesktop || bannerData?.bannerImageMobile,
    )
    const shouldShowCopyButton = showCode && bannerCode
    const shouldShowPromoLink = showImage && bannerData?.bannerLabelLink && bannerData?.bannerLink
    const greyPromoTitleStyle = isGreyPromo ? {color: 'foreground.neutral.default'} : {}
    const redPromoColorStyle = isRedPromo ? {color: 'foreground.negative.default'} : {}
    const greyPromoDescriptionStyle = isGreyPromo ? {color: 'foreground.neutral.muted'} : {}

    const desktopPromoImage = bannerData?.bannerImageDesktop
    const mobilePromoImage = bannerData?.bannerImageMobile
    const mobilePromoImageSrc = mobilePromoImage || desktopPromoImage
    const desktopPromoImageSrc = desktopPromoImage || mobilePromoImage
    const mobilePromoBackgroundImage =
        bannerData?.bannerBackgroundImage?.mobile ||
        bannerData?.backgroundBannerImageMobile ||
        bannerData?.backgroundImageMobile
    const desktopPromoBackgroundImage =
        bannerData?.bannerBackgroundImage?.desktop ||
        bannerData?.backgroundBannerImageDesktop ||
        bannerData?.backgroundImageDesktop
    const hasBackgroundPromoImage = Boolean(
        mobilePromoBackgroundImage || desktopPromoBackgroundImage,
    )
    const shouldShowPromoImage = showImage && mobilePromoImageSrc && !hasBackgroundPromoImage
    const promoImageAlt = headingText || bannerData?.bannerTitle || 'Promo image'
    let promoSummaryBackgroundStyle = {}

    if (shouldApplyBackgroundStyle) {
        promoSummaryBackgroundStyle = getPromoSummaryBackgroundStyle(
            bannerData,
            showImage,
            Boolean(mobilePromoImageSrc),
        )

        if (hasBackgroundPromoImage) {
            promoSummaryBackgroundStyle = {
                bgImage: {
                    base: `url(${mobilePromoBackgroundImage || desktopPromoBackgroundImage})`,
                    md: `url(${desktopPromoBackgroundImage || mobilePromoBackgroundImage})`,
                },
                bgSize: 'cover',
                bgPosition: 'center',
                bgRepeat: 'no-repeat',
            }
        }
    }

    const isFocusLink =
        typeof bannerData?.bannerLink === 'string' && bannerData.bannerLink.startsWith('focus=')
    const focusTarget = isFocusLink ? bannerData?.bannerLink?.split('=')[1]?.trim() : ''

    const renderPromoLink = () => {
        if (!shouldShowPromoLink) {
            return null
        }

        const linkSpacing = {
            pt: ['xxxsmall-1.base', 'xxxsmall-1.sm', 'md', 'xxxsmall-1.lg'],
            ...(shouldShowCopyButton
                ? {mb: ['xxxsmall-1.base', 'xxxsmall-1.sm', 'md', 'xxxsmall-1.lg']}
                : {}),
        }

        if (isFocusLink && focusTarget && onFocusLinkClick) {
            return (
                <Button
                    variant="link-button"
                    size="base"
                    onClick={() => onFocusLinkClick(focusTarget)}
                    {...linkSpacing}
                >
                    {bannerData?.bannerLabelLink}
                </Button>
            )
        }

        return (
            <Button
                as="a"
                href={bannerData?.bannerLink}
                variant="link-button"
                size="base"
                {...linkSpacing}
            >
                {bannerData?.bannerLabelLink}
            </Button>
        )
    }

    const renderPromoTag = () => {
        if (!bannerTag) {
            return null
        }

        return (
            <Flex {...styles.codeContainer} {...styles.promoTagContainer}>
                <Text {...styles.codeText}>{bannerTag}</Text>
            </Flex>
        )
    }

    const PromoDetails = (
        <>
            {(showChevron || (!shouldShowPromoImage && !hasPrimaryBannerImage && !isGreyPromo)) && (
                <PromoIcon
                    {...styles.multiPromoButtonIcon}
                    {...(!showChevron ? redPromoColorStyle : {})}
                />
            )}
            <VStack {...styles.multiPromoButtonContent}>
                {showChevron ? (
                    <Flex {...styles.multiPromoButtonContentFlex}>
                        <VStack {...styles.multiPromoButtonTextStack}>
                            <Flex {...styles.multiPromoButtonLabel}>
                                <Text {...styles.multiPromoButtonLabelText}>
                                    {headingText || (
                                        <>
                                            {multiPromoCount}{' '}
                                            <FormattedMessage
                                                id="promo.available_promotions"
                                                defaultMessage="Available Promotions"
                                            />
                                        </>
                                    )}
                                </Text>
                            </Flex>
                            {bannerDescription && (
                                <Text {...styles.multiPromoButtonDescription}>
                                    {bannerDescription}
                                </Text>
                            )}
                            {renderPromoLink()}
                        </VStack>
                        <Button {...styles.multiPromoButtonChevron} marginInlineStart="auto">
                            <GalleryChevronRightIcon {...styles.multiPromoButtonChevronIcon} />
                        </Button>
                    </Flex>
                ) : (
                    <>
                        {renderPromoTag()}
                        <Flex {...styles.multiPromoButtonLabel}>
                            <Text
                                {...styles.multiPromoButtonLabelText}
                                {...greyPromoTitleStyle}
                                {...redPromoColorStyle}
                            >
                                {headingText || (
                                    <>
                                        {multiPromoCount}{' '}
                                        <FormattedMessage
                                            id="promo.available_promotions"
                                            defaultMessage="Available Promotions"
                                        />
                                    </>
                                )}
                            </Text>
                        </Flex>
                        {bannerDescription && (
                            <Text
                                {...styles.multiPromoButtonDescription}
                                {...greyPromoDescriptionStyle}
                                {...redPromoColorStyle}
                            >
                                {bannerDescription}
                            </Text>
                        )}
                        {renderPromoLink()}
                    </>
                )}
            </VStack>
            {shouldShowCopyButton && (
                <Button
                    {...styles.codeContainer}
                    {...styles.promoCodeContainer}
                    onClick={onCopy}
                    data-testid="product-detail-banner-code"
                >
                    <Text
                        {...styles.codeText}
                        {...(hasCopied ? styles.codeTextCopied : {})}
                        aria-live="polite"
                    >
                        {hasCopied ? (
                            <FormattedMessage id="promo.copied" defaultMessage="Copied" />
                        ) : (
                            bannerCode
                        )}
                    </Text>
                    {hasCopied ? (
                        <CheckIcon {...styles.copyIcon} data-testid="promo-copied-icon" />
                    ) : (
                        <PromoCopyIcon {...styles.copyIcon} />
                    )}
                </Button>
            )}
        </>
    )

    return (
        <VStack
            id="product-detail-banner-content-stack"
            {...styles.multiPromoButtonContentStack}
            {...promoSummaryBackgroundStyle}
        >
            {shouldShowPromoImage ? (
                <Flex {...styles.promoSummaryWithImageLayout}>
                    <Box as="picture" {...styles.promoSummaryImageBox}>
                        <source media="(min-width: 768px)" srcSet={desktopPromoImageSrc} />
                        <Box as="img" src={mobilePromoImageSrc} alt={promoImageAlt} />
                    </Box>
                    <VStack {...styles.promoSummaryDetails}>{PromoDetails}</VStack>
                </Flex>
            ) : (
                PromoDetails
            )}
        </VStack>
    )
}

PromoSummaryContent.propTypes = {
    styles: PropTypes.object.isRequired,
    bannerData: PropTypes.object,
    multiPromoCount: PropTypes.number.isRequired,
    headingText: PropTypes.string,
    bannerDescription: PropTypes.string,
    showChevron: PropTypes.bool,
    showCode: PropTypes.bool,
    bannerCode: PropTypes.string,
    showImage: PropTypes.bool,
    onFocusLinkClick: PropTypes.func,
}

const ProductDetailBannerBlock = ({productBannerInfo, productName, onFocusLinkClick}) => {
    // Call all hooks unconditionally at the top
    const {isOpen, onOpen, onClose} = useDisclosure()
    const styles = useMultiStyleConfig('ProductDetailBannerBlock')

    // Validate productBannerInfo after hooks
    if (!productBannerInfo?.length) {
        return null
    }
    const firstPromo = productBannerInfo[0]
    const multiPromoCount = productBannerInfo.length
    const promoDrawerBodyId = 'promo-drawer-body'

    // AC1: Single promotion - display directly
    if (productBannerInfo.length === 1) {
        return (
            <Box data-testid="single-promo-banner">
                <PromoSummaryContent
                    styles={styles}
                    bannerData={firstPromo}
                    multiPromoCount={multiPromoCount}
                    headingText={firstPromo?.bannerTitle}
                    bannerDescription={firstPromo?.bannerDescription}
                    showChevron={false}
                    showCode={true}
                    bannerCode={firstPromo?.bannerCode}
                    showImage={true}
                    onFocusLinkClick={onFocusLinkClick}
                />
            </Box>
        )
    }

    return (
        <>
            {/* AC3: Custom button for multiple promos */}
            <Box
                onClick={onOpen}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onOpen()
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label="Open promotions drawer"
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={promoDrawerBodyId}
                data-testid="multi-promo-button"
                {...styles.multiPromoButton}
            >
                <PromoSummaryContent
                    styles={styles}
                    bannerData={firstPromo}
                    multiPromoCount={multiPromoCount}
                    bannerDescription={firstPromo?.bannerDescription}
                    showChevron={true}
                    showCode={false}
                    bannerCode={firstPromo?.bannerCode}
                    showImage={false}
                    onFocusLinkClick={onFocusLinkClick}
                />
            </Box>

            {/* AC4A & AC4B: Promo Drawer */}
            <Drawer
                isOpen={isOpen}
                placement="right"
                onClose={onClose}
                size="md"
                data-testid="promo-drawer"
            >
                <DrawerOverlay {...styles.promoDrawerOverlay} />
                <DrawerContent {...styles.promoDrawerBody}>
                    <DrawerHeader data-testid="drawer-header" {...styles.promoDrawerHeader}>
                        <Flex {...styles.promoDrawerHeaderFlex}>
                            <VStack {...styles.promoDrawerHeaderText}>
                                <Text {...styles.promoDrawerHeaderTitle}>
                                    <FormattedMessage
                                        id="promo.drawer_title"
                                        defaultMessage="Promotions"
                                    />
                                </Text>
                                {productName && (
                                    <Text {...styles.promoDrawerHeaderSubtitle}>{productName}</Text>
                                )}
                            </VStack>
                            <DrawerCloseButton
                                data-testid="drawer-close-button"
                                {...styles.promoDrawerCloseButton}
                            />
                        </Flex>
                    </DrawerHeader>
                    <DrawerBody {...styles.promoDrawerBody} id={promoDrawerBodyId}>
                        <VStack {...styles.promoList} data-testid="promo-list">
                            {productBannerInfo.map((bannerData, index) => {
                                const promoVariantStyle = getPromoVariantStyle(bannerData, styles)

                                return (
                                    <Box
                                        key={index}
                                        {...styles.promoCard}
                                        {...promoVariantStyle}
                                        data-testid={`promo-card-${index}`}
                                    >
                                        <PromoSummaryContent
                                            styles={styles}
                                            bannerData={bannerData}
                                            multiPromoCount={index + 1}
                                            headingText={bannerData?.bannerTitle}
                                            bannerDescription={bannerData?.bannerDescription}
                                            showChevron={false}
                                            showCode={true}
                                            bannerCode={bannerData?.bannerCode}
                                            showImage={true}
                                            onFocusLinkClick={onFocusLinkClick}
                                        />
                                    </Box>
                                )
                            })}
                        </VStack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}

ProductDetailBannerBlock.propTypes = {
    productBannerInfo: PropTypes.array,
    productName: PropTypes.string,
    onFocusLinkClick: PropTypes.func,
}

export default ProductDetailBannerBlock
